import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, ExternalLink, Plus, Undo2 } from 'lucide-react';
import { Button, CopyButton } from './primitives';
import { tamexTrackUrl } from '../shared/orderConstants';
import { formatShortDateTime } from '../shared/orderFormat';
import {
  SHIPMENT_DIRECTION,
  directionLabel,
  isReturnTrip,
  newestTrip,
  sortNewestFirst,
} from '../shared/orderShipments';

/*
 * The parcel's trips, newest first, each with the tracking number the courier
 * issued for it.
 *
 * One number per order was wrong the first time a parcel came back: a wrong
 * item goes out, the customer sends it back, the right one goes out — three
 * trips, three numbers, and staff need all of them to answer "where is it?".
 * Shopify lists an order's fulfillments the same way. The newest trip to the
 * customer is the one the storefront and the apps show, and it is marked.
 *
 * Fulfilment is manual: staff create the shipment on Tamex's portal and key
 * the number it hands back in here. There is no courier picker because Tamex
 * is the only courier. Called "tracking number" throughout, never "waybill" —
 * trade jargon that reads as needlessly hard English to the people using this
 * screen. "To customer" / "Return" rather than "outbound" for the same reason.
 *
 * Drawing: this admin has no Tailwind base reset and Bootstrap's `.border` is
 * `!important`, so a one-sided `border-b` or a `border-transparent` turns into
 * a full grey box (see admin_tailwind_border_gotcha). Rows are therefore
 * separated by 1px bars, and the row actions are plain text buttons with
 * `border-0`, never bordered ones.
 *
 * Drafts live here rather than in OrderView, so typing a number never
 * re-renders the items table (the old screen's worst performance problem).
 *
 * Only a non-empty box can save: removing a trip is its own button with its
 * own confirmation, so a slip of the keyboard cannot erase one.
 */

const INPUT_CLASS =
  'w-full h-9 rounded-lg border border-[#D6DFE2] px-2.5 text-[13px] text-nysmah-dark bg-white focus:outline-none focus:border-nysmah-accent focus:ring-1 focus:ring-nysmah-accent';

const TEXT_BUTTON_CLASS =
  'bg-transparent border-0 p-0 text-[11.5px] font-semibold cursor-pointer rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-nysmah-accent';

const DIRECTION = {
  [SHIPMENT_DIRECTION.OUTBOUND]: { Icon: ArrowUpRight, tone: 'text-[#256B4C]' },
  [SHIPMENT_DIRECTION.RETURN]: { Icon: Undo2, tone: 'text-[#8A6508]' },
};

const numberLabel = (direction, courierLabel) =>
  direction === SHIPMENT_DIRECTION.RETURN
    ? `${courierLabel} return tracking number`
    : `${courierLabel} tracking number`;

const Separator = () => (
  <li role="separator" aria-hidden="true" className="h-px bg-[#EEF2F4] my-2.5" />
);

const DirectionTag = ({ direction }) => {
  const { Icon, tone } = DIRECTION[direction] || DIRECTION[SHIPMENT_DIRECTION.OUTBOUND];
  return (
    <span className={`inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.06em] ${tone}`}>
      <Icon className="w-3 h-3" aria-hidden="true" />
      {directionLabel(direction)}
    </span>
  );
};

const DirectionPicker = ({ value, onChange }) => (
  <div
    role="radiogroup"
    aria-label="Which way is the parcel going?"
    className="flex gap-0.5 p-0.5 rounded-lg bg-[#F0F4F5]"
  >
    {[SHIPMENT_DIRECTION.OUTBOUND, SHIPMENT_DIRECTION.RETURN].map((direction) => {
      const selected = value === direction;
      return (
        <button
          key={direction}
          type="button"
          role="radio"
          aria-checked={selected}
          onClick={() => onChange(direction)}
          className={`flex-1 h-7 rounded-md border-0 text-[12px] font-semibold cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-nysmah-accent ${
            selected
              ? 'bg-white text-nysmah-dark shadow-sm'
              : 'bg-transparent text-[#6B7C86] hover:text-nysmah-dark'
          }`}
        >
          {directionLabel(direction)}
        </button>
      );
    })}
  </div>
);

/* A number box with Save and Cancel. Enter saves, Escape cancels. */
const NumberForm = ({
  inputId,
  label,
  placeholder,
  initial = '',
  isSaving,
  onSubmit,
  onCancel,
  children,
}) => {
  const [draft, setDraft] = useState(initial);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const value = draft.trim();
  const submit = () => {
    if (!value || isSaving) return;
    onSubmit(value);
  };

  return (
    <div className="flex flex-col gap-2">
      {children}
      <div>
        <label
          htmlFor={inputId}
          className="block text-[11px] font-semibold text-[#4A5B66] mb-1"
        >
          {label}
        </label>
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          value={draft}
          maxLength={64}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit();
            if (event.key === 'Escape') onCancel();
          }}
          placeholder={placeholder}
          className={INPUT_CLASS}
        />
      </div>
      <div className="flex items-center gap-3">
        <Button variant="primary" size="sm" onClick={submit} disabled={isSaving || !value}>
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className={`${TEXT_BUTTON_CLASS} text-[#6B7C86] hover:text-nysmah-dark disabled:opacity-50`}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const ShipmentRow = ({
  shipment,
  courierLabel,
  isCurrent,
  isEditable,
  isEditing,
  isSaving,
  onStartEdit,
  onSubmitEdit,
  onCancelEdit,
  onRemove,
}) => {
  const isReturn = isReturnTrip(shipment);
  const when = formatShortDateTime(shipment.created_on);
  const meta = shipment.created_by_contact ? `${when} · ${shipment.created_by_contact}` : when;

  return (
    <li className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 flex-wrap min-w-0">
          <DirectionTag direction={shipment.direction} />
          {isCurrent && (
            <span className="inline-flex items-center px-1.5 py-px rounded bg-[#E8F3EE] text-[#256B4C] text-[10px] font-bold">
              Customer sees this
            </span>
          )}
        </span>
        {!isEditing && (
          <a
            href={tamexTrackUrl(shipment.tracking_number)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-0.5 shrink-0 text-[11.5px] font-semibold text-nysmah-accent no-underline hover:underline"
          >
            {isReturn ? 'Track return' : 'Track'}
            <ExternalLink className="w-3 h-3" aria-hidden="true" />
          </a>
        )}
      </div>

      {isEditing ? (
        <NumberForm
          inputId={`shipment-${shipment.id}-number`}
          label={numberLabel(shipment.direction, courierLabel)}
          placeholder={`Paste the number from ${courierLabel}`}
          initial={shipment.tracking_number}
          isSaving={isSaving}
          onSubmit={onSubmitEdit}
          onCancel={onCancelEdit}
        />
      ) : (
        <>
          <span className="flex items-center gap-1 min-w-0">
            <span className="text-[13.5px] font-semibold text-nysmah-dark tabular-nums break-all">
              {shipment.tracking_number}
            </span>
            <CopyButton
              value={shipment.tracking_number}
              label={isReturn ? 'Copy return tracking number' : 'Copy tracking number'}
            />
          </span>
          <div className="flex items-center justify-between gap-3 text-[11.5px] text-[#6B7C86]">
            <span className="min-w-0 truncate" title={meta}>
              {meta}
            </span>
            {isEditable && (
              <span className="inline-flex items-center gap-2.5 shrink-0">
                <button
                  type="button"
                  onClick={onStartEdit}
                  className={`${TEXT_BUTTON_CLASS} text-[#6B7C86] hover:text-nysmah-dark`}
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={onRemove}
                  className={`${TEXT_BUTTON_CLASS} text-[#6B7C86] hover:text-[#C2453D]`}
                >
                  Remove
                </button>
              </span>
            )}
          </div>
        </>
      )}
    </li>
  );
};

const ShipmentList = ({
  shipments,
  courierLabel,
  isEditable,
  onAdd,
  onEdit,
  onRemove,
}) => {
  // null, 'add', or the id of the trip being corrected — one form at a time.
  const [editing, setEditing] = useState(null);
  const [direction, setDirection] = useState(SHIPMENT_DIRECTION.OUTBOUND);
  const [isSaving, setSaving] = useState(false);

  const trips = sortNewestFirst(shipments);
  const current = newestTrip(trips, SHIPMENT_DIRECTION.OUTBOUND);
  const isAdding = editing === 'add';

  const startAdd = () => {
    setDirection(SHIPMENT_DIRECTION.OUTBOUND);
    setEditing('add');
  };
  const close = () => {
    if (!isSaving) setEditing(null);
  };

  const submitAdd = async (number) => {
    setSaving(true);
    const ok = await onAdd(direction, number);
    setSaving(false);
    if (ok) setEditing(null);
  };

  const submitEdit = async (shipment, number) => {
    setSaving(true);
    const ok = await onEdit(shipment, number);
    setSaving(false);
    if (ok) setEditing(null);
  };

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[12px] font-medium text-[#6B7C86] shrink-0">Courier</span>
        <span className="text-[13px] font-semibold text-nysmah-dark">{courierLabel}</span>
      </div>

      {trips.length === 0 && !isAdding && (
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-[12px] font-medium text-[#6B7C86] shrink-0">Status</span>
          <span className="text-[13px] font-semibold text-[#8A6508]">Not shipped yet</span>
        </div>
      )}

      {(trips.length > 0 || isAdding) && (
        <ul className="list-none m-0 p-0 flex flex-col">
          {trips.map((shipment, index) => (
            <React.Fragment key={shipment.id}>
              {index > 0 && <Separator />}
              <ShipmentRow
                shipment={shipment}
                courierLabel={courierLabel}
                isCurrent={current?.id === shipment.id}
                isEditable={isEditable && editing == null}
                isEditing={editing === shipment.id}
                isSaving={isSaving}
                onStartEdit={() => setEditing(shipment.id)}
                onSubmitEdit={(number) => submitEdit(shipment, number)}
                onCancelEdit={close}
                onRemove={() => onRemove(shipment)}
              />
            </React.Fragment>
          ))}
          {isAdding && (
            <>
              {trips.length > 0 && <Separator />}
              <li>
                <NumberForm
                  inputId="new-shipment-number"
                  label={numberLabel(direction, courierLabel)}
                  placeholder={`Paste the number from ${courierLabel}`}
                  isSaving={isSaving}
                  onSubmit={submitAdd}
                  onCancel={close}
                >
                  <DirectionPicker value={direction} onChange={setDirection} />
                </NumberForm>
              </li>
            </>
          )}
        </ul>
      )}

      {isEditable && editing == null && (
        <Button size="sm" onClick={startAdd} className="w-full">
          <Plus className="w-3.5 h-3.5" aria-hidden="true" />
          Add tracking number
        </Button>
      )}
    </div>
  );
};

export default ShipmentList;
